import Fetcher from 'services/Fetcher'
import { API_DICTIONARY, HTTP_METHODS } from 'constants/api'
import {
  ResponseWithPagination,
  TOptionsResponse,
  TOptionsWithNameResponse,
  VALIDATION_MESSAGES
} from '../../constants'
import {
  TArchiveUsersRequest,
  TCommunityHs,
  TContractCategoryItem,
  TDepartmentsResponse,
  TTimezonesItem
} from './types'
import { LedByUnion } from '../../features/ContractCategoryDetails/types'
import { VendorFeeTypeUnion } from '../../features/ContractDetails/types'
import { IUser, TUserResponse } from '../../redux/store/user/types'
import { TListItem } from 'components/Forms/forms.user.d'
import { THealthSystem } from '../../features/HealthSystems/types'

const fetcher = new Fetcher({})

export const fetchUserData = async (uuid: string) =>
  fetcher.request<unknown, TUserResponse>({
    url: `${API_DICTIONARY.USERS}${uuid}/`,
    method: HTTP_METHODS.GET
  })

export const updateUser = async (uuid: string, data) => {
  return await fetcher.request<IUser, TUserResponse>({
    url: `${API_DICTIONARY.USERS}${uuid}/`,
    method: HTTP_METHODS.PATCH,
    successMessage: `User ${VALIDATION_MESSAGES.SM0009} saved.`,
    data
  })
}

export const createUser = async (data) =>
  fetcher.request<IUser, TUserResponse>({
    url: `${API_DICTIONARY.USERS}`,
    method: HTTP_METHODS.POST,
    successMessage: `User ${VALIDATION_MESSAGES.SM0009} created.`,
    data
  })

export const getVendorUserRoleOptions = (vendorId: string) =>
  fetcher.request<unknown, ResponseWithPagination<TOptionsResponse>>({
    url: `${API_DICTIONARY.MY_COMMUNITY}${API_DICTIONARY.VENDORS}${vendorId}/${API_DICTIONARY.USERS}${API_DICTIONARY.OPTIONS}roles/`,
    method: HTTP_METHODS.GET
  })

export const createVendorCommunityUser = (vendorId: string, data) =>
  fetcher.request<IUser, TUserResponse>({
    url: `${API_DICTIONARY.MY_COMMUNITY}${API_DICTIONARY.VENDORS}${vendorId}/${API_DICTIONARY.USERS}`,
    method: HTTP_METHODS.POST,
    successMessage: `User ${VALIDATION_MESSAGES.SM0009} created.`,
    data
  })

export const archiveUsers = async (data) =>
  fetcher.request<TArchiveUsersRequest, unknown>({
    url: `${API_DICTIONARY.USERS}${API_DICTIONARY.BULK_ARCHIVE}`,
    method: HTTP_METHODS.POST,
    successMessage: `User ${VALIDATION_MESSAGES.SM0009} archived.`,
    data
  })

export const fetchHospitals = async (
  communityid: string,
  hsid: string,
  outsideCommunity?: boolean
) =>
  fetcher.request<unknown, ResponseWithPagination<TListItem>>({
    url: outsideCommunity
      ? `${API_DICTIONARY.COMMUNITIES}${API_DICTIONARY.HEALTH_SYSTEMS}${hsid}/${API_DICTIONARY.HOSPITALS}`
      : `${API_DICTIONARY.COMMUNITIES}${communityid}/${API_DICTIONARY.HEALTH_SYSTEMS}${hsid}/${API_DICTIONARY.HOSPITALS}`,
    method: HTTP_METHODS.GET
  })
export const fetchCommunitiesWithHs = async () =>
  fetcher.request<unknown, ResponseWithPagination<TCommunityHs>>({
    url: `${API_DICTIONARY.COMMONS}${API_DICTIONARY.COMMUNITY_HEALTH_SYSTEMS}`,
    method: HTTP_METHODS.GET
  })
export const fetchTimeZonesList = async () =>
  fetcher.request<unknown, TTimezonesItem[]>({
    url: `${API_DICTIONARY.USERS}${API_DICTIONARY.TIMEZONES}`,
    method: HTTP_METHODS.GET
  })
export const fetchDepartments = async (
  communityid: string,
  hsid: string,
  hospitals?: string,
  outsideCommunity?: boolean
) =>
  fetcher.request<unknown, TDepartmentsResponse>({
    url: outsideCommunity
      ? `${API_DICTIONARY.COMMUNITIES}${API_DICTIONARY.HEALTH_SYSTEMS}${hsid}/${API_DICTIONARY.DEPARTMENTS}`
      : `${API_DICTIONARY.COMMUNITIES}${communityid}/${API_DICTIONARY.HEALTH_SYSTEMS}${hsid}/${API_DICTIONARY.DEPARTMENTS}`,
    method: HTTP_METHODS.GET,
    params: { hospitals }
  })

export const fetchContractCategories = async () =>
  fetcher.request<unknown, ResponseWithPagination<TContractCategoryItem>>({
    url: `${API_DICTIONARY.CONTRACT_CATEGORIES}`,
    method: HTTP_METHODS.GET
  })

export const fetchContractCategoriesForContracts = async (
  creation_type?: string
) =>
  fetcher.request<unknown, ResponseWithPagination<TOptionsWithNameResponse>>({
    url: `${API_DICTIONARY.CONTRACTS}${API_DICTIONARY.OPTIONS}${API_DICTIONARY.CATEGORY}`,
    method: HTTP_METHODS.GET,
    params: creation_type ? { creation_type } : {}
  })

export const fetchContractCategoriesForRfp = async (creation_type: string) =>
  fetcher.request<unknown, ResponseWithPagination<TOptionsWithNameResponse>>({
    url: `${API_DICTIONARY.RFP}${API_DICTIONARY.OPTIONS}${API_DICTIONARY.CATEGORY}`,
    method: HTTP_METHODS.GET,
    params: { creation_type: creation_type }
  })

export const fetchPaymentTerms = async () =>
  fetcher.request<unknown, ResponseWithPagination<TOptionsResponse>>({
    url: `${API_DICTIONARY.CONTRACTS}${API_DICTIONARY.OPTIONS}${API_DICTIONARY.PAYMENT_TERM_DAYS}`,
    method: HTTP_METHODS.GET
  })

export const fetchShippingMethods = async () =>
  fetcher.request<unknown, ResponseWithPagination<TOptionsResponse>>({
    url: `${API_DICTIONARY.CONTRACTS}${API_DICTIONARY.OPTIONS}${API_DICTIONARY.SHIPPING_METHOD}`,
    method: HTTP_METHODS.GET
  })

export const fetchPaymentFrequency = async (
  vendor_fee_type?: VendorFeeTypeUnion
) =>
  fetcher.request<unknown, ResponseWithPagination<TOptionsResponse>>({
    url: `${API_DICTIONARY.CONTRACTS}${API_DICTIONARY.OPTIONS}${API_DICTIONARY.PAYMENT_FREQUENCY}`,
    method: HTTP_METHODS.GET,
    params: { vendor_fee_type }
  })

export const fetchVendorFeeTypes = async (led_by?: LedByUnion) =>
  fetcher.request<unknown, ResponseWithPagination<TOptionsResponse>>({
    url: `${API_DICTIONARY.CONTRACTS}${API_DICTIONARY.OPTIONS}${API_DICTIONARY.VENDOR_FEE_TYPE}`,
    method: HTTP_METHODS.GET,
    params: { led_by }
  })

export const fetchLedBy = async () =>
  fetcher.request<unknown, ResponseWithPagination<TOptionsResponse>>({
    url: `${API_DICTIONARY.CONTRACTS}${API_DICTIONARY.OPTIONS}${API_DICTIONARY.LED_BY}`,
    method: HTTP_METHODS.GET
  })

export const getHealthSystem = async (communityUUID: string, HSUUID: string) =>
  fetcher.request<unknown, THealthSystem>({
    url: `${API_DICTIONARY.COMMUNITIES}${communityUUID}/${API_DICTIONARY.HEALTH_SYSTEMS}${HSUUID}/`,
    method: HTTP_METHODS.GET
  })
